/**
 * Privat/Erhverv prisvisning: Erhverv = excl. VAT (×0.8) + "ekskl. moms".
 * localStorage key palledesign_customer_type: private | business
 */
import { formatMoney } from '@theme/money-formatting';
import { ThemeEvents } from '@theme/events';

const STORAGE_KEY = 'palledesign_customer_type';
const VAT_FACTOR = 0.8;

/**
 * @returns {{ moneyFormat: string, currency: string } | null }
 */
function getConfig() {
  const cfg = window.__palledesignB2B;
  if (!cfg?.moneyFormat || !cfg?.currency) return null;
  return cfg;
}

export function isBusinessCustomer() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'business';
  } catch {
    return false;
  }
}

function applyBusinessPricing() {
  const cfg = getConfig();
  if (!cfg || !isBusinessCustomer()) return;

  const { moneyFormat, currency } = cfg;

  document.querySelectorAll('[data-b2b-cents]').forEach((el) => {
    const raw = el.getAttribute('data-b2b-cents');
    if (raw == null || raw === '') return;
    const cents = parseInt(raw, 10);
    if (Number.isNaN(cents) || cents < 0) return;
    const exCents = Math.round(cents * VAT_FACTOR);
    el.textContent = formatMoney(exCents, moneyFormat, currency);
  });

  document.querySelectorAll('.b2b-moms-suffix').forEach((el) => {
    el.hidden = false;
  });

  document.documentElement.classList.add('palledesign-b2b-prices');
}

function applyPrivateDisplay() {
  const cfg = getConfig();
  if (!cfg) return;

  const { moneyFormat, currency } = cfg;

  document.querySelectorAll('[data-b2b-cents]').forEach((el) => {
    const raw = el.getAttribute('data-b2b-cents');
    if (raw == null || raw === '') return;
    const cents = parseInt(raw, 10);
    if (Number.isNaN(cents) || cents < 0) return;
    el.textContent = formatMoney(cents, moneyFormat, currency);
  });

  document.querySelectorAll('.b2b-moms-suffix').forEach((el) => {
    el.hidden = true;
  });

  document.documentElement.classList.remove('palledesign-b2b-prices');
}

function syncAudiencePricingFromStorage() {
  if (isBusinessCustomer()) {
    applyBusinessPricing();
  } else {
    applyPrivateDisplay();
  }
}

function syncAudienceToggle() {
  const roleEl = document.getElementById('audience-footer-role');
  const btn = document.getElementById('audience-type-switch');
  const isBiz = isBusinessCustomer();
  if (roleEl) roleEl.textContent = isBiz ? 'Erhverv' : 'Privat';

  if (btn) {
    btn.textContent = isBiz ? 'Skift til privat' : 'Skift til erhverv';
    btn.setAttribute(
      'aria-label',
      isBiz
        ? 'Skift til privat: vis priser inklusive moms'
        : 'Skift til erhverv: vis priser eksklusive moms'
    );
    btn.setAttribute(
      'title',
      isBiz ? 'Vis priser inkl. moms' : 'Vis priser ekskl. moms'
    );
  }
}

function closeAudienceDialogIfOpen() {
  const dialog = document.getElementById('audience-type-dialog');
  if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
}

function initAudienceToggle() {
  const btn = document.getElementById('audience-type-switch');
  if (!btn) return;

  syncAudienceToggle();

  btn.addEventListener('click', () => {
    const next = isBusinessCustomer() ? 'private' : 'business';
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }

    syncAudiencePricingFromStorage();
    syncAudienceToggle();
    closeAudienceDialogIfOpen();

    window.dispatchEvent(new CustomEvent('palledesign:audience-selected', { detail: { value: next } }));
  });
}

function isThemeDesignMode() {
  return document.documentElement.classList.contains('shopify-design-mode');
}

function openModalIfNeeded() {
  if (isThemeDesignMode()) return;

  let existing;
  try {
    existing = localStorage.getItem(STORAGE_KEY);
  } catch {
    return;
  }

  if (existing === 'private' || existing === 'business') {
    syncAudiencePricingFromStorage();
    return;
  }

  const dialog = document.getElementById('audience-type-dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  dialog.showModal();
}

function saveChoice(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }

  const dialog = document.getElementById('audience-type-dialog');
  if (dialog instanceof HTMLDialogElement) dialog.close();

  syncAudiencePricingFromStorage();
  syncAudienceToggle();

  window.dispatchEvent(new CustomEvent('palledesign:audience-selected', { detail: { value } }));
}

function init() {
  openModalIfNeeded();

  document.getElementById('audience-type-private')?.addEventListener('click', () => saveChoice('private'));
  document.getElementById('audience-type-business')?.addEventListener('click', () => saveChoice('business'));

  initAudienceToggle();

  document.addEventListener(ThemeEvents.variantUpdate, () => {
    requestAnimationFrame(() => {
      syncAudiencePricingFromStorage();
    });
  });

  document.addEventListener(ThemeEvents.cartUpdate, () => {
    requestAnimationFrame(() => {
      syncAudiencePricingFromStorage();
    });
  });

  document.addEventListener('palledesign:b2b-reapply', () => {
    requestAnimationFrame(() => {
      syncAudiencePricingFromStorage();
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
